import { Form, InputNumber } from "antd";
import { useForm } from "antd/es/form/Form";
import PolicySettingsPage, {
  DiffRow,
  fmtBool,
} from "pages/settings/policy/PolicySettingsPage";
import {
  InlineField,
  SettingsSection,
  ToggleRow,
  optionBlockStyle,
} from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";
import { useBusinessSettings } from "hooks/queries/business-settings";

type Values = {
  notify_clash: boolean;
  notify_client_booking: boolean;
  notify_client_waitlist: boolean;
  notify_client_cancel: boolean;
  notify_reward_claim: boolean;
  notify_package_expiry: boolean;
  notify_package_expiry_days?: number;
  notify_low_credit: boolean;
  notify_low_credit_threshold?: number;
};

type ToggleName =
  | "notify_clash"
  | "notify_client_booking"
  | "notify_client_waitlist"
  | "notify_client_cancel"
  | "notify_reward_claim"
  | "notify_package_expiry"
  | "notify_low_credit";

// One switch per notification type, grouped like the feed's filter pills.
// labelKey doubles as the confirm modal's row label.
const GROUPS: {
  labelKey: string;
  toggles: { name: ToggleName; labelKey: string; descKey?: string }[];
}[] = [
  {
    labelKey: "notification_settings.group_schedule",
    toggles: [
      {
        name: "notify_clash",
        labelKey: "notification_settings.clash",
        descKey: "notification_settings.clash_desc",
      },
    ],
  },
  {
    labelKey: "notification_settings.group_client",
    toggles: [
      {
        name: "notify_client_booking",
        labelKey: "notification_settings.client_booking",
        descKey: "notification_settings.client_booking_desc",
      },
      {
        name: "notify_client_waitlist",
        labelKey: "notification_settings.client_waitlist",
        descKey: "notification_settings.client_waitlist_desc",
      },
      {
        name: "notify_client_cancel",
        labelKey: "notification_settings.client_cancel",
        descKey: "notification_settings.client_cancel_desc",
      },
    ],
  },
  {
    labelKey: "notification_settings.group_reward",
    toggles: [
      {
        name: "notify_reward_claim",
        labelKey: "notification_settings.reward_claim",
        descKey: "notification_settings.reward_claim_desc",
      },
    ],
  },
  {
    labelKey: "notification_settings.group_package",
    toggles: [
      {
        name: "notify_package_expiry",
        labelKey: "notification_settings.package_expiry",
        descKey: "notification_settings.package_expiry_desc",
      },
      {
        name: "notify_low_credit",
        labelKey: "notification_settings.low_credit",
        descKey: "notification_settings.low_credit_desc",
      },
    ],
  },
];

const TOGGLE_LABEL_KEYS: Record<ToggleName, string> = Object.fromEntries(
  GROUPS.flatMap((g) => g.toggles.map((t) => [t.name, t.labelKey])),
) as Record<ToggleName, string>;

// GroupLabel / ToggleRow live in settingsRows.tsx, shared with Pembatasan
// Admin so the two settings lists render identically.

// The two package alerts carry a threshold that only renders while their
// toggle is on. AntD keeps unmounted field values in the form store
// (preserve default) and the server keeps the column value while the toggle
// is off, so switching off and back on restores the number.
//
// The line reads as one sentence through the input ("Remind me [7 days]
// before expiry"): labelKey is the words before the box, suffixKey the words
// after it (empty in a language that needs none), and nameKey the noun the
// confirm modal and the aria-label use for the field.
const ThresholdInput = ({
  name,
  labelKey,
  suffixKey,
  nameKey,
  min,
  max,
  unit,
  errorKey,
}: {
  name: "notify_package_expiry_days" | "notify_low_credit_threshold";
  labelKey: string;
  suffixKey: string;
  nameKey: string;
  min: number;
  max: number;
  unit: string;
  errorKey: string;
}) => (
  // The field itself is a noStyle item so its validation message is not
  // printed inside the sentence (where it would widen the box's slot and push
  // the tail words right). The outer, nameless Form.Item collects that
  // message and prints it on its own line under the whole row; the box still
  // takes the red error status from its own item.
  <Form.Item style={{ margin: 0 }}>
    <InlineField label={translate(labelKey)} suffix={translate(suffixKey)}>
      <Form.Item
        name={name}
        noStyle
        rules={[
          {
            required: true,
            type: "integer",
            min,
            max,
            message: translate(errorKey),
          },
        ]}
      >
        <InputNumber
          min={min}
          max={max}
          precision={0}
          style={{ width: 130 }}
          addonAfter={unit}
          aria-label={translate(nameKey)}
        />
      </Form.Item>
    </InlineField>
  </Form.Item>
);

const NotificationSettingsPage = () => {
  const [form] = useForm<Values>();

  // The threshold inputs only render while their toggle is on. The form
  // instance is owned here (not by the shell) so these watches are legal.
  const expiryOn = Form.useWatch("notify_package_expiry", form);
  const lowCreditOn = Form.useWatch("notify_low_credit", form);
  // No waitlist activity to be told about while the studio's waitlist is
  // switched off: the switch is hidden and its stored value kept.
  const { data: settings } = useBusinessSettings();
  const waitlistOff = settings?.waitlist_enabled === false;
  const groups = GROUPS.map((group) => ({
    ...group,
    toggles: group.toggles.filter((t) => !(waitlistOff && t.name === "notify_client_waitlist")),
  }));

  return (
    <PolicySettingsPage<Values>
      sectioned
      capability={AdminCapability.OperationalSettings}
      title={translate("notification_settings.title")}
      confirmNote={translate("notification_settings.confirm_note")}
      form={form}
      initialValues={(s) => ({
        notify_clash: s.notify_clash,
        notify_client_booking: s.notify_client_booking,
        notify_client_waitlist: s.notify_client_waitlist,
        notify_client_cancel: s.notify_client_cancel,
        notify_reward_claim: s.notify_reward_claim,
        notify_package_expiry: s.notify_package_expiry,
        notify_package_expiry_days: s.notify_package_expiry_days,
        notify_low_credit: s.notify_low_credit,
        notify_low_credit_threshold: s.notify_low_credit_threshold,
      })}
      buildSave={(v, s) => {
        const rows: DiffRow[] = [];
        const payload: Partial<Values> = {};

        (Object.keys(TOGGLE_LABEL_KEYS) as ToggleName[]).forEach((name) => {
          const next = !!v[name];
          if (next !== s[name]) {
            rows.push({
              label: translate(TOGGLE_LABEL_KEYS[name]),
              before: fmtBool(s[name]),
              after: fmtBool(next),
            });
            payload[name] = next;
          }
        });

        // A threshold only diffs (and only ships) while its toggle is on;
        // while off, the server keeps the stored value.
        const dayUnit = translate("class_settings.unit.days");
        if (v.notify_package_expiry) {
          const nextDays = Number(v.notify_package_expiry_days);
          if (nextDays !== s.notify_package_expiry_days) {
            rows.push({
              label: translate("notification_settings.package_expiry_days_label"),
              before: `${s.notify_package_expiry_days} ${dayUnit}`,
              after: `${nextDays} ${dayUnit}`,
            });
            payload.notify_package_expiry_days = nextDays;
          }
        }
        const creditUnit = translate("notification_settings.unit_credits");
        if (v.notify_low_credit) {
          const nextThreshold = Number(v.notify_low_credit_threshold);
          if (nextThreshold !== s.notify_low_credit_threshold) {
            rows.push({
              label: translate("notification_settings.low_credit_threshold_label"),
              before: `${s.notify_low_credit_threshold} ${creditUnit}`,
              after: `${nextThreshold} ${creditUnit}`,
            });
            payload.notify_low_credit_threshold = nextThreshold;
          }
        }

        return { rows, payload };
      }}
    >
      {groups.map((group) => (
        <SettingsSection key={group.labelKey} title={translate(group.labelKey)}>
          {group.toggles.map((t, idx) => (
            // One block per option: the toggle row plus (for the two
            // package alerts) its threshold, so the hairline rule between
            // options always separates whole options.
            <div key={t.name} style={optionBlockStyle(idx === group.toggles.length - 1)}>
              <ToggleRow {...t} />
              {t.name === "notify_package_expiry" && expiryOn && (
                <ThresholdInput
                  name="notify_package_expiry_days"
                  labelKey="notification_settings.package_expiry_days"
                  suffixKey="notification_settings.package_expiry_days_suffix"
                  nameKey="notification_settings.package_expiry_days_label"
                  min={1}
                  max={90}
                  unit={translate("class_settings.unit.days")}
                  errorKey="notification_settings.days_error"
                />
              )}
              {t.name === "notify_low_credit" && lowCreditOn && (
                <ThresholdInput
                  name="notify_low_credit_threshold"
                  labelKey="notification_settings.low_credit_threshold"
                  suffixKey="notification_settings.low_credit_threshold_suffix"
                  nameKey="notification_settings.low_credit_threshold_label"
                  min={1}
                  max={50}
                  unit={translate("notification_settings.unit_credits")}
                  errorKey="notification_settings.credits_error"
                />
              )}
            </div>
          ))}
        </SettingsSection>
      ))}
    </PolicySettingsPage>
  );
};

export default NotificationSettingsPage;
